import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { login as loginApi } from '../api/auth';
import { LoadingScreenGlobal } from '../pages/other/LoadingScreen';
import { AuthContextType, Association, User } from '../types/authContext.types';
import * as Auth from '../api/auth';
import * as AssociationAPI from '../api/association';
import { setBaseAPIAssociationId, setBaseAPIToken } from '../api/api.base';
import { useDevice } from './deviceContext';
import { isSuccessCode } from '../helpers/apiResponse';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [token, setToken] = useState<string>("");
    const [association, setAssociation] = useState<Association>({ id: 0, name: "" });
    const [memberships, setMemberships] = useState<number[]>([]);
    const [validatingSession, setValidatingSession] = useState(true);
    const [associationRole, setAssociationRole] = useState<string>("");
    const { validatingDevice } = useDevice();

    const [user, setUser] = useState<User>({
        email: "",
        id: 0,
        name: "",
        profile_picture: "",
        role: [],
        status: false,
        surname: "",
    });

    useEffect(() => {
        const savedAssociation = parseInt(window.localStorage.getItem('association_id') || "0");
        const savedToken = window.localStorage.getItem('token');

        const loginF = async (localToken: string, localAssociation: number) => {
            try {
                const response = await loginApi(localToken);
                if (!isSuccessCode(response.error_code)) {
                    setIsAuthenticated(false);
                    setValidatingSession(false);
                }
                setBaseAPIToken(localToken);
                setBaseAPIAssociationId(`${localAssociation}`);

                AssociationAPI.detail(localAssociation ?? 0).then(res => {
                    if (isSuccessCode(res.error_code)) {
                        setAssociation({
                            id: res.data.id,
                            name: res.data.name,
                            icon: res.data.icon,
                            created_at: res.data.created_at,
                            member_count: res.data.member_count,
                        });

                        setUser(response.data);
                        setToken(localToken);
                        setBaseAPIToken(localToken);
                        setIsAuthenticated(true);
                        setAssociationRole(res.data.role);
                        setValidatingSession(false);
                    }
                });

            } catch (error) {
                console.error("Error verifying session:", error);
            }
        };

        if (validatingDevice) {
            return;
        }

        if (savedToken) {
            loginF(savedToken, savedAssociation)
        } else {
            setValidatingSession(false);
        }
    }, [validatingDevice]);

    useEffect(() => {
        if (association.icon) {
            const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
            if (link) {
                link.href = association.icon;
            } else {
                const newLink = document.createElement('link');
                newLink.rel = 'icon';
                newLink.href = association.icon;
                document.head.appendChild(newLink);
            }
        }
    }, [association.icon]);

    const login = (newToken: string, association_id: number, refreshToken: string) => {
        window.localStorage.setItem('token', newToken);
        window.localStorage.setItem('refresh_token', refreshToken);
        if (association_id) {
            window.localStorage.setItem('association_id', association_id.toString());
            setBaseAPIAssociationId(`${association_id}`);
        }
        setBaseAPIToken(newToken);
        setToken(newToken);
        window.location.assign("/");
    };

    const changeAssociation = (association_id: number) => {
        window.localStorage.setItem('association_id', association_id.toString());
        setBaseAPIAssociationId(`${association_id}`);
        window.location.assign("/");
    };

    const logout = () => {
        setIsAuthenticated(false);
        Auth.logout();
        setToken("");
        setBaseAPIToken("");
        setBaseAPIAssociationId("");
        setAssociationRole("");
        setAssociation({ id: 0, name: "" });
        setMemberships([]);
        window.localStorage.removeItem('token');
        window.localStorage.removeItem('association_id');
        window.localStorage.removeItem('gre');
        //window.location.href = "/auth"; // Redirect alla pagina di login
    };

    if (validatingSession) return <LoadingScreenGlobal />;

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout, token, user, memberships, association, changeAssociation, associationRole, validatingSession }}>
            {children}
        </AuthContext.Provider>
    );
};

// Updated RequireAuth component
export const RequireAuth = ({ children }: { children: ReactNode }) => {
    const authData = useAuth();

    if (authData.validatingSession) {
        console.log("Validating session...");
        return <LoadingScreenGlobal />;
    }

    console.log("Session validation complete. Authenticated:", authData.isAuthenticated);


    if (!authData.isAuthenticated) {
        return <Navigate to="/auth" />;
    }

    return <>{children}</>;
};
